/**
 * Permission Error Boundary
 * Catches permission errors and provides role switching options
 */

import React from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class PermissionErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a permission-related error
    const isPermissionError = error.message.includes('Unauthorized') || 
                             error.message.includes('permission') ||
                             error.message.includes('access');
    
    return { 
      hasError: isPermissionError, 
      error: isPermissionError ? error : null 
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Permission error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <PermissionErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

const PermissionErrorFallback: React.FC<{ error: Error | null }> = ({ error }) => {
  const resetToAdmin = useMutation(api.users.resetToAdmin);
  const [isResetting, setIsResetting] = React.useState(false);

  const handleResetToAdmin = async () => {
    setIsResetting(true);
    try {
      const result = await resetToAdmin();
      if (result.success) {
        window.location.reload();
      } else {
        alert(`Failed to reset: ${result.error}`);
      }
    } catch (err) {
      console.error('Failed to reset to admin:', err);
      alert('Failed to reset to admin. Please refresh the page.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div style={{
      padding: '2rem',
      margin: '2rem',
      background: '#fff3cd',
      border: '1px solid #ffeaa7',
      borderRadius: '8px',
      textAlign: 'center'
    }}>
      <h3>Permission Error</h3>
      <p>You don't have permission to access this feature.</p>
      <p><strong>Error:</strong> {error?.message}</p>
      
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={handleResetToAdmin}
          disabled={isResetting}
          style={{
            padding: '0.5rem 1rem',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            marginRight: '1rem',
            cursor: isResetting ? 'not-allowed' : 'pointer'
          }}
        >
          {isResetting ? 'Resetting...' : 'Reset to Admin'}
        </button>
        
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1rem',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Refresh Page
        </button>
      </div>
      
      <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
        <p>Use the role switcher at the top of the page to change your access level.</p>
      </div>
    </div>
  );
};

export default PermissionErrorBoundary;